import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import {
  StepDetailSchema,
  documentPagePicturePath,
  type AnswerStep,
  type StepDetail,
} from "@gb/contracts";
import { RegionFrame, usePagePicture } from "@/features/brain/region-overlay";
import { DISPLAY_LOCALE } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * What one step did, under its row (2026-09-26, Phase 5; §3 of
 * `plans/phase-5-answering-design.md`, "Detail under a row").
 *
 * **Code writes every word here, from the stored structure.** Numbers and
 * dates are the reader's (`DISPLAY_LOCALE`); titles, names, search terms and
 * the code are shown as written; "Why:" is the model's own few words, as
 * written. Nothing here reads a title or a term to decide what to show: the
 * shape comes from the step's `tool`.
 *
 * A detail that does not parse costs the row its detail, never the row: it
 * says "No detail for this step". A failed step shows only its safe message.
 * Nothing is cut: a code run's output is shown whole, in a panel that scrolls
 * (the 20,000-character cap of the first design is gone, G25).
 */

/** Code shown before the rest is folded away. A display fold: one click shows all. */
export const CODE_LINES_FOLDED = 12;

function num(n: number): string {
  return new Intl.NumberFormat(DISPLAY_LOCALE).format(n);
}

function counted(n: number, one: string, many: string): string {
  return `${num(n)} ${n === 1 ? one : many}`;
}

function day(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(DISPLAY_LOCALE, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

/** How long a step took, or null while it runs or when a time is missing. */
export function stepDuration(step: Pick<AnswerStep, "startedAt" | "finishedAt">): string | null {
  if (!step.finishedAt) return null;
  const ms = Date.parse(step.finishedAt) - Date.parse(step.startedAt);
  if (!Number.isFinite(ms) || ms < 0) return null;
  if (ms < 10_000) {
    return `${new Intl.NumberFormat(DISPLAY_LOCALE, { maximumFractionDigits: 1 }).format(ms / 1000)} s`;
  }
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${num(seconds)} s`;
  const rest = seconds % 60;
  return rest === 0 ? `${num(Math.floor(seconds / 60))} min` : `${num(Math.floor(seconds / 60))} min ${num(rest)} s`;
}

export function StepDetailView({
  step,
  projectId,
  onCite,
}: {
  step: AnswerStep;
  projectId: string;
  /** Scrolls to a cited source's card under the answer, as a chip in the answer does. */
  onCite: (n: number) => void;
}) {
  const took = stepDuration(step);
  const parsed = StepDetailSchema.safeParse(step.detail);

  return (
    <div className="flex flex-col gap-2 text-[0.8rem] leading-[1.5] text-ink-slate">
      <p className="flex flex-wrap gap-x-3 text-muted">
        <span>{step.outcome === "running" ? "Running" : took ? `Took ${took}` : "Finished"}</span>
        {step.purpose && (
          <span>
            <span className="font-semibold text-ink">Why:</span> {step.purpose}
          </span>
        )}
      </p>
      {step.outcome === "error" ? (
        // The safe message only. Never a stack trace, never model output.
        <p>{step.failure ?? "This step failed."}</p>
      ) : step.outcome === "repeat" ? (
        <p>The same call as step {num(step.repeatOf ?? 0)}. It was not run again.</p>
      ) : step.outcome === "timeout" ? (
        <p>
          Stopped at its time limit
          {step.timeoutSeconds !== null ? ` of ${num(step.timeoutSeconds)} s` : ""}.
        </p>
      ) : step.outcome === "step-limit" || step.outcome === "cancelled" ? null : parsed.success ? (
        <DetailBody detail={parsed.data} projectId={projectId} onCite={onCite} />
      ) : step.kind === "tool" || step.kind === "context" ? (
        <p className="text-muted">No detail for this step.</p>
      ) : null}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <p>
      <span className="text-muted">{label}:</span> {children}
    </p>
  );
}

function Quoted({ children }: { children: string }) {
  return <span className="font-semibold text-ink">“{children}”</span>;
}

function fileHref(projectId: string, documentId: string, page?: number | null): string {
  const href = `/projects/${projectId}/brain/files/${documentId}`;
  return page ? `${href}?page=${page}` : href;
}

function FileLink({ projectId, documentId, title, page }: { projectId: string; documentId: string; title: string; page?: number | null }) {
  return (
    <Link to={fileHref(projectId, documentId, page)} target="_blank" rel="noreferrer" className="font-semibold text-ink hover:text-brand">
      {title}
    </Link>
  );
}

function DetailBody({
  detail,
  projectId,
  onCite,
}: {
  detail: StepDetail;
  projectId: string;
  onCite: (n: number) => void;
}) {
  switch (detail.tool) {
    case "context":
      return (
        <>
          {/* The categories that hold a file, of the tenant's list
              (`document-ai/0032`). */}
          <Fact label="Files">
            {counted(detail.files, "file", "files")} in {counted(detail.categories, "category", "categories")}
          </Fact>
          <Fact label="The journal">
            {counted(detail.journalEntries, "entry", "entries")}, {counted(detail.journalNotes, "note", "notes")},{" "}
            {counted(detail.photos, "photo", "photos")} ·{" "}
            <Link to={`/projects/${projectId}/journal`} className="font-semibold text-ink hover:text-brand">
              Open the journal
            </Link>
          </Fact>
          <Fact label="Decisions">{num(detail.decisions)}</Fact>
          {detail.leftOut && (
            <Fact label="Left out because they did not fit">
              {counted(detail.leftOut.journalEntries, "journal entry", "journal entries")},{" "}
              {counted(detail.leftOut.journalNotes, "note", "notes")} and{" "}
              {counted(detail.leftOut.decisions, "decision", "decisions")}
              {detail.leftOut.before ? `, from before ${day(detail.leftOut.before)}` : ""}. A search still finds them.
            </Fact>
          )}
          {detail.unfiledDictations > 0 && (
            <p>
              {counted(detail.unfiledDictations, "note dictation", "note dictations")} not yet filed{" "}
              {detail.unfiledDictations === 1 ? "is" : "are"} not in it.
            </p>
          )}
          <Fact label="Size">{counted(detail.chars, "character", "characters")}</Fact>
        </>
      );
    case "search":
      return (
        <>
          <Fact label="Terms">
            {detail.terms.map((term, index) => (
              <span key={index}>
                {index > 0 && ", "}
                <Quoted>{term}</Quoted>
              </span>
            ))}
          </Fact>
          <Fact label="Searched">
            {detail.scope === "category" && detail.scopeName !== null ? (
              <>
                the files in <Quoted>{detail.scopeName}</Quoted>
              </>
            ) : detail.scope === "files" && detail.scopeFiles !== null ? (
              counted(detail.scopeFiles, "chosen file", "chosen files")
            ) : (
              "all files"
            )}
            {": "}
            {counted(detail.filesSearched, "file", "files")}
            {detail.everyVisibleFile ? ", every file the Brain may read" : ""}
          </Fact>
          <Fact label="Found">
            {counted(detail.hits, "hit", "hits")} in {counted(detail.filesHit, "file", "files")}
          </Fact>
          {detail.unplaced > 0 && (
            <p>
              {counted(detail.unplaced, "hit", "hits")} could not be cut into a snippet. {detail.unplaced === 1 ? "It is" : "They are"} counted.
            </p>
          )}
          {detail.results.length > 0 && (
            <ul className="flex flex-col gap-0.5">
              {detail.results.map((result) => (
                <li key={result.documentId} className="flex gap-2">
                  <FileLink projectId={projectId} documentId={result.documentId} title={result.title} />
                  <span className="text-muted">{counted(result.hits, "hit", "hits")}</span>
                </li>
              ))}
            </ul>
          )}
          {detail.pages > 1 && <p className="text-muted">Results page {num(detail.page)} of {num(detail.pages)}</p>}
        </>
      );
    case "open_file":
      return (
        <>
          <Fact label="File">
            <FileLink
              projectId={projectId}
              documentId={detail.documentId}
              title={detail.title}
              page={detail.part === "pages" || detail.part === "reading" ? detail.from : null}
            />
          </Fact>
          {detail.from !== null && (
            <Fact label={detail.part === "reading" ? "Reading of page" : `${capitalise(detail.part)} read`}>
              {detail.to !== null && detail.to !== detail.from ? `${num(detail.from)}–${num(detail.to)}` : num(detail.from)}
              {detail.total !== null ? ` of ${num(detail.total)}` : ""}
            </Fact>
          )}
          {detail.passages.length > 0 && (
            <ul className="flex flex-col gap-1">
              {detail.passages.map((passage, index) => (
                <li key={index} className="flex items-center gap-2">
                  {passage.n !== null ? (
                    <button
                      type="button"
                      onClick={() => onCite(passage.n!)}
                      aria-label={`Show source ${passage.n}`}
                      className="inline-flex h-[18px] min-w-[18px] cursor-pointer items-center justify-center rounded-full border border-brand/40 bg-brand/10 px-[5px] font-display text-[0.66rem] font-semibold text-brand hover:bg-brand hover:text-white"
                    >
                      {passage.n}
                    </button>
                  ) : null}
                  <span>{passage.citation}</span>
                  <span className={cn("text-[0.72rem]", passage.n !== null ? "font-semibold text-ok" : "text-muted")}>
                    {passage.n !== null ? "cited" : "not cited"}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {detail.notShown.length > 0 && (
            <div>
              <p className="text-muted">Not shown by this step:</p>
              <ul className="list-disc pl-5">
                {detail.notShown.map((line, index) => (
                  <li key={index}>{line}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      );
    case "list_files":
      return (
        <>
          {(detail.category !== null || detail.folder !== null) && (
            <Fact label={detail.category !== null ? "Category" : "Folder"}>
              <Quoted>{(detail.category ?? detail.folder)!}</Quoted>
            </Fact>
          )}
          <Fact label="Listed">
            {num(detail.listed)} of {counted(detail.total, "file", "files")}
          </Fact>
        </>
      );
    case "versions":
      return (
        <>
          <Fact label="File">
            <FileLink projectId={projectId} documentId={detail.documentId} title={detail.title} />
          </Fact>
          <Fact label="Versions">
            {num(detail.versions)}, {num(detail.current)} current
          </Fact>
          {detail.hidden > 0 && <Fact label="Hidden from the Brain, not read">{num(detail.hidden)}</Fact>}
        </>
      );
    case "links":
      return (
        <>
          <Fact label="File">
            <FileLink projectId={projectId} documentId={detail.documentId} title={detail.title} />
          </Fact>
          <Fact label="Linked">{counted(detail.links, "file", "files")}</Fact>
          {detail.pages > 1 && <p className="text-muted">Page {num(detail.page)} of {num(detail.pages)}</p>}
        </>
      );
    case "metadata": {
      const filter = detail.filter;
      return (
        <>
          <Fact label="Found">{counted(detail.matches, detail.photos ? "photo" : "file", detail.photos ? "photos" : "files")}</Fact>
          {filter.takenFrom !== null && (
            <Fact label="Taken">
              {filter.takenTo !== null && filter.takenTo !== filter.takenFrom
                ? `${day(filter.takenFrom)} to ${day(filter.takenTo)}`
                : day(filter.takenFrom)}
            </Fact>
          )}
          {filter.author !== null && (
            <Fact label="Author">
              <Quoted>{filter.author}</Quoted>
            </Fact>
          )}
          {filter.sender !== null && (
            <Fact label="Sender">
              <Quoted>{filter.sender}</Quoted>
            </Fact>
          )}
          {filter.field !== null && (
            <Fact label="Field">
              <Quoted>{filter.field}</Quoted>
              {filter.value !== null && (
                <>
                  {" "}
                  is <Quoted>{filter.value}</Quoted>
                </>
              )}
            </Fact>
          )}
        </>
      );
    }
    case "crew":
      return (
        <Fact label={`As of ${day(detail.asOf)}`}>
          {counted(detail.people, "person", "people")}, {num(detail.notCleared)} not cleared
        </Fact>
      );
    case "findings":
      return <p>Findings are not made yet.</p>;
    case "kept_looks":
      return (
        <>
          <Fact label="Kept looks">
            {num(detail.listed)} listed, {num(detail.opened)} read
          </Fact>
          {detail.target.documentId !== null && detail.target.title !== null && (
            <Fact label="Of">
              <FileLink projectId={projectId} documentId={detail.target.documentId} title={detail.target.title} page={detail.target.page} />
              {detail.target.page !== null ? `, page ${num(detail.target.page)}` : ""}
            </Fact>
          )}
        </>
      );
    case "look":
      return <LookDetail detail={detail} projectId={projectId} />;
    case "rows_for_code":
      return (
        <>
          <Fact label="File">
            <FileLink projectId={projectId} documentId={detail.documentId} title={detail.title} />
            {detail.sheet !== null && (
              <>
                , sheet <Quoted>{detail.sheet}</Quoted>
              </>
            )}
          </Fact>
          <Fact label="Given to code">
            {counted(detail.rows, "row", "rows")} × {counted(detail.columns, "column", "columns")}, as <code>{detail.file}</code>
          </Fact>
        </>
      );
    case "code_execution":
      return <CodeDetail detail={detail} />;
  }
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function LookDetail({ detail, projectId }: { detail: Extract<StepDetail, { tool: "look" }>; projectId: string }) {
  const target = detail.target;
  return (
    <>
      {/* The region on the page it was read from. A picture's or a journal
          photo's region is on that picture, which has no route by page, so it
          is given in words. */}
      {detail.box && target.documentId !== null && target.page !== null && (target.kind === "drawing" || target.kind === "page") ? (
        <LookRegion projectId={projectId} documentId={target.documentId} page={target.page} box={detail.box} />
      ) : detail.box ? (
        <Fact label="Region">
          {Math.round(detail.box.x * 100)}–{Math.round((detail.box.x + detail.box.w) * 100)}% across,{" "}
          {Math.round(detail.box.y * 100)}–{Math.round((detail.box.y + detail.box.h) * 100)}% down
        </Fact>
      ) : null}
      {detail.width !== null && detail.height !== null && (
        <Fact label="Sent">
          {num(detail.width)} × {num(detail.height)} px
          {detail.scale !== null && detail.scale !== 1 ? `, at ${new Intl.NumberFormat(DISPLAY_LOCALE, { maximumFractionDigits: 2 }).format(detail.scale)} of full size` : ""}
          {detail.model ? ` to ${detail.model}` : ""}
        </Fact>
      )}
      {detail.reason && <Fact label="Why a new look">{detail.reason}</Fact>}
      <p className="text-muted">{detail.lookId ? "Kept with the file, for the next question." : "Not kept."}</p>
    </>
  );
}

function LookRegion({
  projectId,
  documentId,
  page,
  box,
}: {
  projectId: string;
  documentId: string;
  page: number;
  box: NonNullable<Extract<StepDetail, { tool: "look" }>["box"]>;
}) {
  const url = usePagePicture(documentPagePicturePath(projectId, documentId, page));
  if (!url) return null;
  return (
    <div>
      <RegionFrame box={box}>
        <img src={url} alt={`Page ${page}, with the region looked at marked`} className="block h-auto max-h-[280px] w-auto max-w-full" />
      </RegionFrame>
    </div>
  );
}

function CodeDetail({ detail }: { detail: Extract<StepDetail, { tool: "code_execution" }> }) {
  const [unfolded, setUnfolded] = useState(false);
  const lines = detail.code.split("\n");
  const folded = !unfolded && lines.length > CODE_LINES_FOLDED;
  return (
    <>
      <pre className="overflow-x-auto rounded-input bg-surface-sunken px-3 py-2 font-mono text-[0.74rem] leading-[1.5] text-ink">
        {folded ? lines.slice(0, CODE_LINES_FOLDED).join("\n") : detail.code}
      </pre>
      {lines.length > CODE_LINES_FOLDED && (
        <button
          type="button"
          onClick={() => setUnfolded((now) => !now)}
          className="self-start text-[0.75rem] font-semibold text-muted hover:text-brand"
        >
          {unfolded ? "Fold the code" : `Show all ${num(lines.length)} lines`}
        </button>
      )}
      <p className="text-muted">
        Output{detail.exitCode !== null ? `, exit code ${num(detail.exitCode)}` : ""}:
      </p>
      {/* Whole, in a panel that scrolls. Never cut. */}
      <pre
        data-testid="code-output"
        className="max-h-[320px] overflow-auto rounded-input bg-surface-sunken px-3 py-2 font-mono text-[0.74rem] leading-[1.5] whitespace-pre-wrap text-ink"
      >
        {detail.output}
      </pre>
      {detail.files.length > 0 && <Fact label="Files">{detail.files.join(", ")}</Fact>}
    </>
  );
}
